<template>
    <div class="outer_legal_container">
        <template v-if="data != null">
            <template v-if="is_checkbox_mode">
                <div class="cb_container">
                    <v-checkbox
                        v-model="cb_value"
                        v-on:change="onChangeCheckBox"
                        :required="required"
                        aria-labelledby="legal_agreement_label"
                        :disabled="data.allow_signature == false"
                        hide-details
                    />
                </div>
                <div
                    id="legal_agreement_label"
                    :class="{ 'label_container': true, 'label_container_disabled': !data.allow_signature }"
                    v-on:click="onClickLabel"
                >
                    <span
                        v-for="(item, index) in data.segments"
                        :key="'seg_' + index"
                    >
                        <a
                            v-if="item.type == 'link'"
                            @click.stop
                            :href="item.url"
                            target="_blank"
                        >{{ item.text }}</a>
                        <span v-else>
                            {{ item.text }}
                        </span>
                    </span>
                </div>
            </template>

            <template v-else-if="is_written_mode">
                <div class="written-attestation-container">
                    <div
                        class="written-agreement-text"
                        :class="{ 'label_container_disabled': !data.allow_signature }"
                        v-on:click="onClickLabel"
                    >
                        <span
                            v-for="(item, index) in data.segments"
                            :key="'seg_' + index"
                        >
                            <a
                                v-if="item.type == 'link'"
                                @click.stop
                                :href="item.url"
                                target="_blank"
                            >{{ item.text }}</a>
                            <span v-else>{{ item.text }}</span>
                        </span>
                    </div>

                    <div
                        class="written-input-container"
                        v-on:click="onClickLabel"
                        :class="{
                            'disabled-click-area': !data.allow_signature,
                            'label_container_disabled': !data.allow_signature
                        }"
                    >
                        <v-text-field
                            v-model="written_value"
                            v-on:input="onWrittenInput"
                            :required="required"
                            :disabled="data.allow_signature == false"
                            :label="getFormattedLabel"
                            placeholder="Type text to confirm"
                            :error="written_value && !isValidSpecimen(written_value)"
                            hide-details="auto"
                            dense
                        />
                    </div>
                </div>
            </template>
        </template>
        <template v-else-if="loading">
            <div class="loading-container">
                <v-progress-circular
                    indeterminate
                    color="primary"
                    size="20"
                    width="2"
                ></v-progress-circular>
                <span class="loading-text">Loading agreement...</span>
            </div>
        </template>
    </div>
</template>

<script>
const _ = require('lodash');
import enums_data from '@/js/enums-data.json'

const agreement_request_queues = {};

function enqueueAgreementRequest(queue_key, request_fn)
{
    const normalized_key = queue_key || '_global_';
    const last_chain = agreement_request_queues[normalized_key] || Promise.resolve();

    const current_chain = last_chain.catch(() => null).then(() => request_fn());

    const handled_chain = current_chain.then(
        (result) => {
            if (agreement_request_queues[normalized_key] === handled_chain) {
                delete agreement_request_queues[normalized_key];
            }
            return result;
        },
        (error) => {
            if (agreement_request_queues[normalized_key] === handled_chain) {
                delete agreement_request_queues[normalized_key];
            }
            throw error;
        }
    );

    agreement_request_queues[normalized_key] = handled_chain;
    return handled_chain;
}

export default {
    props: {
        app_id: {
            type: String,
            default: function () { return null; }
        },

        type: {
            type: String,
            default: function () { return 'bulk-message-attestation'; }
        },

        organization_id: {
            type: String,
            default: function () { return null; }
        },

        organization_label: {
            type: String,
            default: function () { return null; }
        },

        override_patterns: {
            type: Array,
            default: function () { return null; }
        },

        required: {
            type: Boolean,
            default: function () { return false; }
        },
    },

    data() {
        return {
            cb_value: false,
            written_value: '',
            retry_count: 0,
            max_retries: 3,
            retry_timeout: null,
            data: null,
            loading: false,
            error: null,
        };
    },

    mounted() {
        this.cb_value = false;
        this.written_value = '';
        this.doUpdate();
    },

    computed: {
        agreement_type_config() {
            if (!this.type) return null

            const legal_agreement_types = enums_data.legal_agreement_type || []
            return legal_agreement_types.find(item => item.value === this.type)
        },

        input_type() {
            return this.agreement_type_config?.input_type || 'checkbox-consent'
        },

        expected_input() {
            return this.agreement_type_config?.expected_input || null
        },

        is_written_mode() {
            return this.input_type === 'written-consent'
        },

        is_checkbox_mode() {
            return this.input_type === 'checkbox-consent'
        },

        getExpectedWrittenText() {
            if (!this.is_written_mode || !this.expected_input || !this.data?.plain_text_specimen) {
                return null
            }
            return this.expected_input;
        },

        getFormattedLabel() {
            return `Type "${this.getExpectedWrittenText}" to be able to continue`;
        }

    },

    methods: {
        update: _.debounce(function () {
            this.retry_count = 0;
            if (this.retry_timeout) {
                clearTimeout(this.retry_timeout);
                this.retry_timeout = null;
            }
            this.doUpdate();
        }, 200),

        requestAgreement() {
            if (!this.type || !this.organization_id) {
                return Promise.resolve(null);
            }

            return new Promise((resolve, reject) => {
                this.$rc.rest({
                    'spinner': false,
                    'endpoint': 'PUT: /api/v1/legal/get-agreement',
                    'params': {
                        'type': this.type,
                        'organization_id': this.organization_id,
                        'organization_label': this.organization_label,
                        'override_patterns': this.override_patterns || [],
                        'app_id': this.app_id
                    },
                    'on_success': (data) => resolve(data),
                    'on_error': (error) => reject(error),
                });
            });
        },

        fetchAgreement() {
            const queue_key = `org_${this.organization_id || 'unknown'}`;
            enqueueAgreementRequest(queue_key, () => {
                this.loading = true;
                this.error = null;
                return this.requestAgreement();
            })
                .then((data) => {
                    if (!data) {
                        return;
                    }
                    this.$nextTick(() => {
                        this.data = data;
                        this.retry_count = 0;
                        this.loading = false;
                        this.error = null;
                    });
                })
                .catch((error) => {
                    this.error = error;
                    this.loading = false;
                    if (this.retry_count < this.max_retries) {
                        this.retry_count++;
                        this.retry_timeout = setTimeout(() => this.doUpdate(), 200);
                    }
                });
        },

        doUpdate() {
            if (!this.organization_id || this.organization_id === '') {
                if (this.retry_count < this.max_retries) {
                    this.retry_count++;
                    this.retry_timeout = setTimeout(() => this.doUpdate(), 200);
                }
                return;
            }
            this.fetchAgreement();
        },

        onClickLabel() {
            if (this.data.allow_signature == false) {
                this.$rc.alert('Unable to Sign Agreement', this.data.allow_signature_explanation);
                return;
            }

            if (this.is_checkbox_mode) {
                this.cb_value = !this.cb_value;
                this.$emit('input', this.cb_value);
            }
        },

        onChangeCheckBox() {
            this.$emit('input', this.cb_value);
        },

        onWrittenInput() {
            const is_valid = this.isValidSpecimen(this.written_value);
            this.$emit('input', is_valid);
        },

        getSpecimen() {
            if (this.is_checkbox_mode) {
                return this.data?.plain_text_specimen || null;
            } else if (this.is_written_mode) {
                return this.written_value || null;
            }
            return null;
        },

        isValidSpecimen(specimen) {
            if (!this.is_written_mode) {
                return true
            }

            if (!specimen || !this.expected_input || !this.data?.plain_text_specimen) {
                return false
            }

            const expected_text = this.expected_input;
            return specimen.trim() === expected_text
        },

        getSubmissionSpecimen(current_ui_value) {
            if (this.is_checkbox_mode) {
                return this.data?.plain_text_specimen || null
            } else if (this.is_written_mode) {
                return current_ui_value || null
            }
            return null
        }
    },

    beforeDestroy() {
        if (this.retry_timeout) {
            clearTimeout(this.retry_timeout);
            this.retry_timeout = null;
        }
    },

    watch: {
        organization_id(new_value, old_value) {
            if (new_value && new_value !== old_value) {
                this.update();
            }
        },

        organization_label(new_value, old_value) {
            if (new_value && new_value !== old_value) {
                this.update();
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.outer_legal_container {
    display: flex;
    align-items: top;
    min-height: 56px;
}

.cb_container {
    flex-grow: 0;
}

.label_container {
    flex-grow: 1;
    padding-top: 19px;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;
}

.label_container_disabled {
    color: rgba(0, 0, 0, 0.2);
}

.label_container_disabled a {
    color: rgba(0, 0, 0, 0.2);
}

.loading-container {
    display: flex;
    align-items: center;
    padding: 16px 0;
    width: 100%;
}

.loading-text {
    margin-left: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
}

.written-attestation-container {
    width: 100%;
    padding-top: 12px;
}

.written-agreement-text {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 18px;
}

.written-agreement-text.label_container_disabled,
.written-agreement-text.label_container_disabled a {
    color: rgba(0, 0, 0, 0.2);
    cursor: pointer; 
}


.written-input-container {
    width: 100%;
}

.written-input-container.disabled-click-area {
    cursor: pointer;
}

.written-input-container.label_container_disabled {
    ::v-deep .v-label {
        color: rgba(0, 0, 0, 0.2) !important;
    }
}
</style>
